import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AppointmentsPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [appointmentData, setAppointmentData] = useState({
    customer_email: user.email,
    customer_name: user.full_name,
    customer_phone: user.phone || "",
    vendor_id: "",
    appointment_date: "",
    service_type: "tailor",
    notes: ""
  });

  useEffect(() => {
    fetchAppointments();
  }, []);

  const fetchAppointments = async () => {
    try {
      const url = user.role === 'vendor'
        ? `${API}/appointments?vendor_id=${user.id}`
        : `${API}/appointments?customer_email=${user.email}`;
      const response = await axios.get(url);
      setAppointments(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des rendez-vous");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/appointments`, appointmentData);
      toast.success("Rendez-vous créé avec succès!");
      setShowForm(false);
      fetchAppointments();
    } catch (error) {
      toast.error("Erreur lors de la création du rendez-vous");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <nav className="bg-white border-b border-border/40 px-4 md:px-8 lg:px-12 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img
              src="/woma-logo.png"
              alt="ARTEKNOS Logo"
              className="h-12 w-12 rounded-full object-cover ring-2 ring-[#D4AF37]/40 shadow-lg shadow-[#D4AF37]/20"
              data-testid="arteknos-logo"
            />
            <h1 className="text-xl md:text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif', color: 'hsl(var(--primary))'}}>
              Livraison Express
            </h1>
          </div>
          <div className="flex gap-4">
            <Button onClick={() => navigate(user.role === 'vendor' ? '/vendor' : '/dashboard')} variant="outline" className="rounded-full" data-testid="appointments-back-btn">Retour</Button>
            <Button onClick={onLogout} variant="outline" className="rounded-full" data-testid="appointments-logout-btn">Déconnexion</Button>
          </div>
        </div>
      </nav>

      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8 flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold mb-2" style={{fontFamily: 'Outfit, sans-serif'}} data-testid="appointments-title">Mes rendez-vous</h2>
              <p className="text-muted-foreground">Gérez vos rendez-vous avec les artisans</p>
            </div>
            {user.role === 'client' && (
              <Button
                onClick={() => setShowForm(!showForm)}
                className="bg-primary hover:bg-primary/90 text-white rounded-full"
                data-testid="toggle-appointment-form"
              >
                {showForm ? "Annuler" : "Nouveau rendez-vous"}
              </Button>
            )}
          </div>

          {showForm && user.role === 'client' && (
            <Card className="mb-8 shadow-xl">
              <CardHeader>
                <CardTitle data-testid="new-appointment-form-title">Prendre rendez-vous</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer-name">Nom complet</Label>
                      <Input
                        id="customer-name"
                        value={appointmentData.customer_name}
                        onChange={(e) => setAppointmentData({...appointmentData, customer_name: e.target.value})}
                        required
                        data-testid="appointment-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer-phone">Téléphone</Label>
                      <Input
                        id="customer-phone"
                        type="tel"
                        value={appointmentData.customer_phone}
                        onChange={(e) => setAppointmentData({...appointmentData, customer_phone: e.target.value})}
                        required
                        data-testid="appointment-phone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="vendor-id">ID de l'artisan/vendeur</Label>
                    <Input
                      id="vendor-id"
                      placeholder="Entrez l'ID de l'artisan"
                      value={appointmentData.vendor_id}
                      onChange={(e) => setAppointmentData({...appointmentData, vendor_id: e.target.value})}
                      required
                      data-testid="appointment-vendor-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="appointment-date">Date et heure</Label>
                    <Input
                      id="appointment-date"
                      type="datetime-local"
                      value={appointmentData.appointment_date}
                      onChange={(e) => setAppointmentData({...appointmentData, appointment_date: e.target.value})}
                      required
                      data-testid="appointment-date-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service-type">Type de service</Label>
                    <select
                      id="service-type"
                      className="h-12 w-full rounded-lg border-input bg-background px-3 py-2 text-sm"
                      value={appointmentData.service_type}
                      onChange={(e) => setAppointmentData({...appointmentData, service_type: e.target.value})}
                      data-testid="appointment-service-select"
                    >
                      <option value="tailor">Artisan</option>
                      <option value="consultation">Consultation</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="notes">Notes (optionnel)</Label>
                    <Textarea
                      id="notes"
                      placeholder="Détails spécifiques..."
                      value={appointmentData.notes}
                      onChange={(e) => setAppointmentData({...appointmentData, notes: e.target.value})}
                      rows={3}
                      data-testid="appointment-notes-textarea"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-primary hover:bg-primary/90 text-white rounded-full py-6"
                    data-testid="submit-appointment-btn"
                  >
                    Confirmer le rendez-vous
                  </Button>
                </form>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle data-testid="appointments-list-title">{user.role === 'vendor' ? 'Rendez-vous reçus' : 'Mes rendez-vous'}</CardTitle>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <p className="text-center py-8 text-muted-foreground">Chargement...</p>
              ) : appointments.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground" data-testid="no-appointments-message">Aucun rendez-vous pour le moment</p>
              ) : (
                <div className="space-y-4">
                  {appointments.map((appointment) => (
                    <div key={appointment.id} className="border border-border/40 rounded-xl p-4 hover:shadow-md transition-all" data-testid={`appointment-${appointment.id}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-semibold text-lg">{appointment.customer_name}</p>
                          <p className="text-sm text-muted-foreground">{appointment.service_type}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          appointment.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                          appointment.status === 'completed' ? 'bg-green-100 text-green-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {appointment.status === 'scheduled' ? 'Planifié' : appointment.status === 'completed' ? 'Terminé' : 'Annulé'}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          {new Date(appointment.appointment_date).toLocaleDateString('fr-FR')}
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          {new Date(appointment.appointment_date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      {appointment.notes && (
                        <p className="mt-2 text-sm text-muted-foreground">Note: {appointment.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
